/**
 * KVKK notice filtering and caching helpers.
 *
 * KVKK (Kişisel Verilerin Korunması Kanunu) is Turkey's data protection law.
 * When the backend streams a response containing a KVKK notice, we:
 * 1. Strip the KVKK block from the visible bot text
 * 2. Show a lightweight notice on first encounter (per account)
 * 3. Mark it as shown in localStorage to avoid repeat notices
 */

const KVKK_STORAGE_KEY = 'gengage_kvkk_shown';
const KVKK_TEXT_MARKERS = ['kvkk', 'kişisel veri', 'kisisel veri'];
const KVKK_LAW_NUMBER_RE = /\b6698\b/;

interface KvkkBlockOptions {
  fallbackToFirstBlock?: boolean;
}

export function containsKvkk(html: string): boolean {
  const lower = html.toLowerCase();
  return KVKK_TEXT_MARKERS.some((m) => lower.includes(m)) || KVKK_LAW_NUMBER_RE.test(lower);
}

export function isKvkkShown(accountId: string): boolean {
  try {
    return localStorage.getItem(`${KVKK_STORAGE_KEY}_${accountId}`) === '1';
  } catch {
    return false;
  }
}

export function markKvkkShown(accountId: string): void {
  try {
    localStorage.setItem(`${KVKK_STORAGE_KEY}_${accountId}`, '1');
  } catch {
    // localStorage unavailable — silently ignore
  }
}

/**
 * Strip the KVKK portion from bot HTML.
 * KVKK is typically wrapped in a leading block element.
 * We remove the first block-level element that contains a KVKK marker.
 * Explicit backend KVKK events may omit marker words, so callers can fall
 * back to the leading block while marker fallback remains strict.
 */
export function stripKvkkBlock(html: string, options: KvkkBlockOptions = {}): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const body = doc.body;
  const children = Array.from(body.children);
  let removed = false;
  for (const child of children) {
    if (containsKvkk(child.textContent ?? '')) {
      child.remove();
      removed = true;
      break; // Only strip the first KVKK block
    }
  }
  if (!removed && options.fallbackToFirstBlock === true && children[0]) {
    children[0].remove();
  }
  return body.innerHTML.trim();
}

export function extractKvkkBlock(html: string, options: KvkkBlockOptions = {}): string | null {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const children = Array.from(doc.body.children);
  for (const child of children) {
    if (containsKvkk(child.textContent ?? '')) {
      return child.outerHTML;
    }
  }
  if (options.fallbackToFirstBlock === true) {
    return children[0]?.outerHTML ?? null;
  }
  return null;
}

const LOCALE_TO_LANGUAGE: Record<string, string> = {
  tr: 'TURKISH',
  en: 'ENGLISH',
  de: 'GERMAN',
  fr: 'FRENCH',
};

export function localeToOutputLanguage(locale?: string): string {
  if (!locale) return 'TURKISH';
  return LOCALE_TO_LANGUAGE[locale.toLowerCase().slice(0, 2)] ?? 'TURKISH';
}
